import React from 'react';
import {
  PAPUserContext,
  getPAPUser,
  setPAPUser,
} from 'js/sign-components/common/pap-user-store';

/**
 * Mirrors a user into the module-level PAP user store so non-React call
 * sites (Redux thunks, direct logPAPEvent imports) pick up the current
 * user's identifiers automatically.
 */
export function useSyncPAPUserStore(
  user: { guid: string; dbxUserId?: string | null } | null | undefined,
): void {
  const guid = user?.guid;
  const dbxUserId = user?.dbxUserId;

  const ownedRef = React.useRef<PAPUserContext | null>(null);
  const priorRef = React.useRef<PAPUserContext | null | undefined>(undefined);

  // Sync during render, not in useEffect: React runs child passive effects
  // before parent's, so a useEffect here would fire after mount-time
  // logPAPEvent calls in descendants and miss their identifiers. Under
  // Suspense/startTransition a discarded render can leave the store
  // updated without a matching cleanup; the next committed render
  // overwrites it, so the leak is bounded.
  if (guid) {
    const owned = ownedRef.current;
    const userChanged =
      owned === null || owned.guid !== guid || owned.dbxUserId !== dbxUserId;
    if (userChanged) {
      if (priorRef.current === undefined) {
        priorRef.current = getPAPUser();
      }
      const next: PAPUserContext = { guid, dbxUserId };
      setPAPUser(next);
      ownedRef.current = next;
    }
  }

  React.useEffect(() => {
    return () => {
      if (getPAPUser() === ownedRef.current) {
        setPAPUser(priorRef.current ?? null);
      }
    };
  }, []);
}
